import { useCallback, useMemo } from 'react'
import { csvCodec, oneOfCodec, useUrlState } from '../../hooks/useUrlState'
import { daysFilterList } from './class/ClassAnalyticsWidgets'
import { calculateDateRange } from '../../utils/dateUtils'

const EMPTY = []
const DAYS_CODES = daysFilterList.map((d) => d.code)
const DEFAULT_DAYS = daysFilterList[0]

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const isoDateCodec = {
    parse: (raw) => (ISO_DATE.test(raw) ? raw : undefined),
    serialize: String,
}

/**
 * The year/class/day-range filters shared by the student, counsellor and
 * organization analytics screens, mirrored into the URL.
 *
 * Replaces util.js's parseUrlToState/buildUrlFromState, which had three flaws
 * this shape removes by construction:
 *
 *  - They were not inverses. parse produced flat { field: 'classes.id' } items
 *    but build only collected class codes from nested arrays, so a ?classId=
 *    link only round-tripped because build happened to reseed itself from the
 *    existing query string.
 *  - build was additive only: its param writes sat inside `if (!all)`, so
 *    clearing a filter back to "all" could never clear ?year=/?all=, and the
 *    stale value was re-parsed on the next reload.
 *  - Each screen then ran a second effect that rebuilt the query string from a
 *    render-captured location to add ?days=, so the two writers could overwrite
 *    each other.
 *
 * Here every param is its own useUrlState, each doing a read-modify-write
 * against the live history.location. Writes to distinct keys commute, so no
 * ordering between them matters.
 */
/**
 * Just the day-range half. Split out because the organization screen's filter
 * is a three-level institution/year/class scope whose items carry an
 * institution_id and server-supplied `field` values -- it cannot round-trip
 * through ?year=/?classId= without silently collapsing to "all", which is why
 * its sync was left unfinished rather than wired to the class-based helpers.
 */
export const useDaysUrlFilter = () => {
    const [daysCode, setDaysCode] = useUrlState('days', DEFAULT_DAYS.code, oneOfCodec(DAYS_CODES))
    // Only meaningful when daysCode === 'custom'. Without these a custom range
    // survived as ?days=custom and silently reopened as a one-year range.
    const [from, setFrom] = useUrlState('from', '', isoDateCodec)
    const [to, setTo] = useUrlState('to', '', isoDateCodec)

    const daysFilter = useMemo(() => {
        const base = daysFilterList.find((d) => d.code === daysCode) ?? DEFAULT_DAYS
        if (base.code === 'custom' && from && to) return { ...base, startDate: from, endDate: to }
        return { ...base, ...calculateDateRange(base) }
    }, [daysCode, from, to])

    const setDaysFilter = useCallback(
        (next) => {
            const custom = next?.code === 'custom'
            setDaysCode(next?.code ?? DEFAULT_DAYS.code)
            setFrom(custom ? next?.startDate ?? '' : '')
            setTo(custom ? next?.endDate ?? '' : '')
        },
        [setDaysCode, setFrom, setTo]
    )

    return { daysFilter, setDaysFilter }
}

export const useAnalyticsUrlFilters = () => {
    const { daysFilter, setDaysFilter } = useDaysUrlFilter()
    const [years, setYears] = useUrlState('year', EMPTY, csvCodec())
    const [classIds, setClassIds] = useUrlState('classId', EMPTY, csvCodec())

    const selectedOptions = useMemo(
        () => ({
            // Derived, not stored: `all` is exactly "nothing is filtered", so a
            // separate ?all= param could only ever contradict the other two.
            all: years.length === 0 && classIds.length === 0,
            selected: [...years.map((code) => ({ field: 'classes.year', code })), ...classIds.map((code) => ({ field: 'classes.id', code }))],
        }),
        [years, classIds]
    )

    const setSelectedOptions = useCallback(
        (next) => {
            const nextYears = []
            const nextClassIds = []
            if (!next?.all) {
                for (const item of next?.selected ?? []) {
                    if (Array.isArray(item)) {
                        // Nested one level means class-level selections, and every
                        // sub-item's code is a class id -- same rule the previous
                        // buildUrlFromState used.
                        for (const sub of item) nextClassIds.push(sub.code)
                    } else if (item?.field === 'classes.year') {
                        nextYears.push(item.code)
                    } else if (item?.field === 'classes.id') {
                        // Flat class items: what our own parse produces. The old
                        // helper ignored these, so ?classId= only round-tripped
                        // by accident; handling them makes the pair inverse.
                        nextClassIds.push(item.code)
                    }
                }
            }
            setYears(nextYears)
            setClassIds(nextClassIds)
        },
        [setYears, setClassIds]
    )

    return { selectedOptions, setSelectedOptions, daysFilter, setDaysFilter }
}
